import React, { useContext, useMemo, useRef, useState } from 'react'
import { defineMessages, FormattedMessage, MessageDescriptor, useIntl } from 'react-intl'
import { View } from 'react-native'
import { Popover } from 'react-native-popper'

import styled from 'styled-components/native'

import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { Display } from '../../atoms/display/Display'
import { CheckboxGroup } from '../../atoms/formElements/CheckboxGroup'
import { ComposeIcon } from '../../atoms/icons/ComposeIcon'
import { FilterIcon } from '../../atoms/icons/FilterIcon'
import { NumberBadge } from '../../atoms/icons/NumberBadge'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { AppContext } from '../../context'
import { DisplaySize } from '../../styles/typeStyles'
import { ConversationFilter } from '../../ui-models/messages/Conversations'
import { tID } from '../../utils/utils'

const Container = styled.View(({ theme }) => ({
  flexDirection: 'row',
  height: '75px',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing['12px'],
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))
const IconContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['12px'],
}))

const PopoverContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: '16px',
  padding: theme.spacing['16px'],
  gap: theme.spacing['16px'],
  boxShadow: `0 2px 10px ${theme.colors.shadowLow}`,
}))

const ComposeButton = styled(SecondaryButton)({
  // accounts for border sizing causing 1.5px shift
  // can't use box-sizing due to RN not supporting it
  marginTop: '1.5px',
})

const NumberBadgeContainer = styled(NumberBadge)({
  position: 'absolute',
  top: 0,
  right: 0,
})

export type ChatSideBarHeaderProps = {
  onComposePressed: () => void
  filters: ConversationFilter[]
  onFiltersChanged: (filters: ConversationFilter[]) => void
}

export const filterNames: Record<ConversationFilter, MessageDescriptor> = defineMessages({
  [ConversationFilter.ACTIVE]: {
    defaultMessage: 'Active',
    description: 'Label for filtering conversations by active',
  },
  [ConversationFilter.UNREAD]: {
    defaultMessage: 'Unread',
    description: 'Label for filtering conversations by unread',
  },
  [ConversationFilter.UNSCHEDULED]: {
    defaultMessage: 'Unscheduled',
    description: 'Label for filtering conversations by unscheduled',
  },
  [ConversationFilter.LYRA_AI_ENABLED]: {
    defaultMessage: 'Lyra AI Enabled',
    description: 'Label for filtering conversations by if ai coach is enabled',
  },
})

export const ChatSideBarHeader: React.FC<ChatSideBarHeaderProps> = ({
  onComposePressed,
  filters,
  onFiltersChanged,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const ref = useRef(null)
  const { formatMessage } = useIntl()
  const { showAiCoach } = useContext(AppContext)

  // Only show lyra ai enabled filter if ai coach is enabled
  const availableFilters = useMemo(() => {
    if (showAiCoach) {
      return Object.values(ConversationFilter)
    }
    return Object.values(ConversationFilter).filter((filter) => filter !== ConversationFilter.LYRA_AI_ENABLED)
  }, [showAiCoach])

  return (
    <>
      <Container>
        <View ref={ref}>
          <View>
            <TertiaryIconButton
              testID={tID('ChatSideBarHeader-filter-button')}
              size={ButtonSize.UNSIZED}
              leftIcon={
                <IconContainer>
                  <FilterIcon size={24} />
                </IconContainer>
              }
              onPress={() => setIsPopoverOpen((prev) => !prev)}
              accessibilityLabel={formatMessage({
                defaultMessage: 'Filter conversations',
                description: 'Accessibility label for button to filter chat conversations',
              })}
            />
            {filters.length ? <NumberBadgeContainer number={filters.length} /> : null}
          </View>
        </View>
        <ComposeButton
          testID={tID('ChatSideBarHeader-compose-button')}
          leftIcon={<ComposeIcon size={24} />}
          text={<FormattedMessage defaultMessage='Compose' description='Button to start a conversation' />}
          onPress={onComposePressed}
        />
      </Container>
      <Popover
        trigger={ref}
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement='bottom left'
        offset={8}
        animated={!window.Cypress}
      >
        <Popover.Content>
          <PopoverContainer>
            <Display
              size={DisplaySize.EYEBROW}
              text={<FormattedMessage defaultMessage='Filter by' description='Filter conversation popover' />}
            />
            <CheckboxGroup
              baseInputStyle={{ marginBottom: 0 }}
              options={availableFilters.map((filter) => ({
                id: filter,
                text: <FormattedMessage {...filterNames[filter]} />,
              }))}
              name='filters'
              value={filters}
              onChange={(selectedFilters) => onFiltersChanged(selectedFilters as ConversationFilter[])}
            />
          </PopoverContainer>
        </Popover.Content>
      </Popover>
    </>
  )
}
